import React, { useEffect, useState, useRef } from "react";
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "../common/page_component/index.js";
import Loader from "../common/Loader.js";
import Alert from "../common/Alert.js";
import Button from "../common/Button.js";
import RowActionsMenu from "../common/RowActionsMenu.js";
import agentPartnershipService from "../../services/agentPartnershipService.js";
import { useAuth } from "../../context/AuthContext.js";
import isExternalNetworkEnabled from "../../utils/networkMode.js";
import { Inbox, UserPlus } from "lucide-react";
import { useTranslation } from 'react-i18next';


const PartnerAgentList = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [partnerships, setPartnerships] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!isExternalNetworkEnabled(user)) {
      navigate("/admin-dashboard");
      return;
    }
    fetchPartnerships();
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchPartnerships = async () => {
    try {
      setLoading(true);
      const response = await agentPartnershipService.list({ status: "accepted" });
      setPartnerships(response.data?.data || []);
    } catch (error) {
      Alert.error(error.response?.data?.error || "Failed to load partnerships");
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (partnershipId) => {
    const confirmed = await Alert.confirm("Revoke this partnership?");
    if (!confirmed) return;
    try {
      await agentPartnershipService.revoke(partnershipId);
      Alert.success("Partnership revoked");
      fetchPartnerships();
    } catch (error) {
      Alert.error(error.response?.data?.error || "Failed to revoke partnership");
    }
  };

  if (loading) return <DashboardLayout><Loader /></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-4">
        <ListPageHeader title={t('pages.agentNetwork.partnerAgents')} titleClassName="text-xl font-semibold">
          <Button mobileIconOnly variant="secondary" icon={Inbox} onClick={() => navigate("/partner-agents/requests")}>
            Requests
          </Button>
          <Button mobileIconOnly variant="primary" icon={UserPlus} onClick={() => navigate("/partner-agents/find")}>
            {t('pages.agentNetwork.findAgent')}
          </Button>
        </ListPageHeader>
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-4 py-3">Agent</th>
                <th className="px-4 py-3">City</th>
                <th className="px-4 py-3">Home Company</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {partnerships.length === 0 ? (
                <tr><td colSpan="5" className="px-4 py-6 text-center text-gray-500">No active partnerships</td></tr>
              ) : partnerships.map((item) => (
                <tr key={item.partnership_id} className="border-t">
                  <td className="px-4 py-3">{item.agent_name} ({item.agent_user_id})</td>
                  <td className="px-4 py-3">{item.service_city}</td>
                  <td className="px-4 py-3">{item.agent_home_company_name}</td>
                  <td className="px-4 py-3 capitalize">{item.status}</td>
                  <td className="px-4 py-3">
                    <RowActionsMenu
                      isOpen={activeDropdown === item.partnership_id}
                      onToggle={() => setActiveDropdown(activeDropdown === item.partnership_id ? null : item.partnership_id)}
                      onClose={() => setActiveDropdown(null)}
                      menuRef={dropdownRef}
                      extraItems={[
                        {
                          label: "Revoke",
                          icon: "🚫",
                          variant: "danger",
                          onClick: () => handleRevoke(item.partnership_id),
                        },
                      ]}
                      className="relative inline-block"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PartnerAgentList;
